import { useLingui } from '@lingui/react/macro';
import { useMemo, useState } from 'react';

import { scheduleToDays, toDisplayDayIndex } from '@/domain/templates/day-of-week';
import { resolveInstanceCount } from '@/domain/templates/staffing';
import { calculateShiftDuration } from '@/domain/templates/template-utils';
import type { ShiftSet } from '@/types/template';

import { CollapsiblePreviewSection } from './collapsible-preview-section';

/**
 * Read-only template-side staff-group view (WW12). Mirrors the Shift
 * Type tab's collapsible-row layout so the two preview modes feel
 * consistent: one row per section (Open shifts, Auto-assigned, then
 * one per staff group), each with a header that exposes the section
 * name plus its rolled-up shift count and total weekly hours.
 *
 * Sectioning rules:
 * - `staffing.method === 'open'` → Open shifts
 * - `staffing.method === 'automatic'` → Auto-assigned
 * - `staffing.method === 'specific'` → the `Other` section (specific
 *   shifts assign a named employee and carry no group filters)
 */
export function TemplateStaffGroupView({ shifts }: TemplateStaffGroupViewProps) {
  const { t } = useLingui();
  const sections = useMemo(
    () => buildSections(shifts, t`Open shifts`, t`Auto-assigned`),
    [shifts, t],
  );
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  function toggle(key: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <div className="divide-y">
      {sections.map((section) => (
        <CollapsiblePreviewSection
          key={section.key}
          name={section.name}
          perDay={section.perDay}
          totalShifts={section.totalShifts}
          totalHours={section.totalHours}
          collapsed={collapsed.has(section.key)}
          onToggle={() => toggle(section.key)}
          rowTestId={`template-staff-group-section-${section.key}`}
          cellTestIdPrefix={`template-staff-group-cell-${section.key}`}
        />
      ))}
    </div>
  );
}

/**
 * Buckets the template's shifts into one logical section per group
 * (plus the two unassigned categories) and rolls each section's pill
 * matrix + total counts. Shifts are expanded by `getInstanceCount`,
 * matching how the Shift Type tab counts pills per day.
 */
function buildSections(
  shifts: ShiftSet[],
  openLabel: string,
  autoLabel: string,
): ReadonlyArray<Section> {
  const open = emptySection('open', openLabel);
  const auto = emptySection('auto', autoLabel);
  const groups = new Map<string, Section>();

  for (const shift of shifts) {
    const instances = resolveInstanceCount(shift);
    const hoursPerInstance = calculateShiftDuration(shift.startTime, shift.endTime);
    const dayIndices = scheduleToDays(shift.schedule).map(toDisplayDayIndex);

    let target: Section;
    if (shift.staffing.method === 'open') {
      target = open;
    } else if (shift.staffing.method === 'automatic') {
      target = auto;
    } else {
      // `specific` shifts assign a named employee and carry no group
      // filters, so they all bucket into a single "Other" section.
      const groupKey = 'other';
      const groupName = 'Other';
      target = groups.get(groupKey) ?? emptySection(`group-${groupKey}`, groupName);
      groups.set(groupKey, target);
    }

    for (const dayIndex of dayIndices) {
      for (let i = 0; i < instances; i++) {
        target.perDay[dayIndex].push(shift);
      }
      target.totalShifts += instances;
      target.totalHours += hoursPerInstance * instances;
    }
  }

  const sections: Section[] = [];
  if (open.totalShifts > 0) sections.push(open);
  if (auto.totalShifts > 0) sections.push(auto);
  for (const group of [...groups.values()].sort((a, b) => a.name.localeCompare(b.name))) {
    sections.push(group);
  }
  return sections;
}

/** Builds an empty section with seven empty per-day buckets. */
function emptySection(key: string, name: string): Section {
  return {
    key,
    name,
    perDay: Array.from({ length: 7 }, () => [] as ShiftSet[]),
    totalShifts: 0,
    totalHours: 0,
  };
}

interface Section {
  /** Stable key used for collapse state + testids. */
  key: string;
  name: string;
  perDay: ShiftSet[][];
  totalShifts: number;
  totalHours: number;
}

interface TemplateStaffGroupViewProps {
  shifts: ShiftSet[];
}
